<template>
  <div>
    <h6>Enrolled credentials</h6>
    <table class="table table-sm">
      <thead>
        <tr><th>Method</th><th>Enrolled</th><th>By</th><th></th></tr>
      </thead>
      <tbody>
        <tr v-if="!credentials.length"><td colspan="4" class="text-muted">No credentials enrolled.</td></tr>
        <tr v-for="c in credentials" :key="c.id">
          <td>{{ methodLabel(c.method) }}</td>
          <td>{{ formatDate(c.enrolledUtc) }}</td>
          <td>{{ c.enrolledBy }}</td>
          <td class="text-right">
            <b-button size="sm" variant="outline-danger" @click="revoke(c)">Revoke</b-button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Badge enrollment -->
    <h6 class="mt-3">Enroll a badge</h6>
    <div class="pi-reader-status" :class="`pi-status-${badgeStatus}`">
      <span class="pi-dot"></span>
      <span>{{ badgeStatusText }}</span>
    </div>
    <div v-if="capturedBadge" class="text-success font-weight-bold mt-1">Badge captured.</div>
    <b-form-checkbox v-model="manualEntry" class="mt-2">Enter badge ID manually</b-form-checkbox>
    <b-form-input v-if="manualEntry" v-model="manualBadge" class="mt-2" placeholder="Badge ID" autocomplete="off" />
    <div class="mt-2">
      <b-button variant="primary" :disabled="enrolling || !effectiveBadge" @click="enrollBadge">Enroll Badge</b-button>
    </div>

    <!-- Fingerprint enrollment (HID DigitalPersona 4500, Non-WBF driver + dpUruNet SDK) -->
    <h6 class="mt-4">Enroll a fingerprint</h6>
    <div class="pi-reader-status" :class="`pi-status-${fingerprintStatus}`">
      <span class="pi-dot"></span>
      <span>{{ fingerprintStatusText }}</span>
    </div>

    <!-- Idle: gate on biometric privacy consent first, then offer multi-sample capture -->
    <div v-if="enrollMode === 'idle'" class="mt-2">
      <!-- Policy not configured for this tenant -->
      <div v-if="consentStatus && !consentStatus.policyConfigured" class="alert alert-warning small mb-0">
        Fingerprint enrollment is disabled until the biometric privacy policy is configured for this
        tenant. Set the policy version and disclosure text in
        <strong>Configuration → Compliance → Biometric Privacy</strong>.
      </div>

      <!-- Subject has not yet consented under the current policy version -->
      <template v-else-if="consentStatus && !consentStatus.hasActiveConsent">
        <p class="text-muted small mb-2">
          Before fingerprint enrollment, this staff member must read and consent to the current
          biometric privacy disclosure.
        </p>
        <b-button variant="primary" @click="showConsentDialog = true">
          <i class="fas fa-clipboard-check mr-1" /> Record Biometric Consent
        </b-button>
      </template>

      <!-- Active consent — proceed to multi-sample capture -->
      <template v-else>
        <p class="text-muted small mb-2">
          Enrollment captures 5 presentations of the same finger from slightly different angles. The
          reader will consolidate them into a single high-quality template.
        </p>
        <b-button
          variant="primary"
          :disabled="enrolling || fingerprintStatus !== 'connected'"
          @click="startEnrollFingerprint">
          <i class="fas fa-fingerprint mr-1" /> Start fingerprint enrollment
        </b-button>
      </template>
    </div>

    <!-- Collecting or submitting: show progress checklist -->
    <div v-else class="mt-2 pi-enroll-panel">
      <div class="pi-enroll-progress">
        <template v-if="enrollMode === 'submitting'">
          <b-spinner small class="mr-1" /> Consolidating samples…
        </template>
        <template v-else>
          Capture {{ enrollSamples.length }} of {{ ENROLL_STEPS.length }}
        </template>
      </div>
      <ul class="pi-enroll-steps">
        <li
          v-for="(step, idx) in ENROLL_STEPS"
          :key="step.id"
          :class="{
            'pi-step-done': idx < enrollSamples.length,
            'pi-step-current': idx === enrollSamples.length && enrollMode === 'collecting',
          }">
          <span class="pi-step-marker">
            <i v-if="idx < enrollSamples.length" class="fas fa-check text-success" />
            <i v-else-if="idx === enrollSamples.length && enrollMode === 'collecting'" class="fas fa-fingerprint text-primary" />
            <span v-else class="pi-step-dot"></span>
          </span>
          {{ step.label }}
        </li>
      </ul>
      <div class="mt-2">
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="enrollSamples.length === 0 || enrollMode !== 'collecting'"
          @click="restartCurrentStep">
          Restart this step
        </b-button>
        <b-button
          size="sm"
          variant="outline-danger"
          :disabled="enrollMode === 'submitting'"
          class="ml-2"
          @click="cancelEnrollment">
          Cancel
        </b-button>
      </div>
    </div>

    <BiometricConsentDialog
      v-if="consentStatus && consentStatus.policyConfigured"
      :show.sync="showConsentDialog"
      :user-name="userName"
      :policy-version="consentStatus.currentPolicyVersion"
      :document-id="consentStatus.currentDocumentId"
      @consented="onConsented" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as signalR from '@microsoft/signalr';
import PositiveIdService, { PositiveIdCredential, PositiveIdMethod, BiometricConsentStatus } from '@/services/PositiveIdService';
import { buildClientServiceHub, BadgeTapEvent, FingerprintCapturedEvent } from '@/services/ClientServiceHub';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import BiometricConsentDialog from '@/components/Common/BiometricConsentDialog.vue';

type ReaderState = 'idle' | 'connecting' | 'connected' | 'unavailable';
type EnrollMode = 'idle' | 'collecting' | 'submitting';

interface EnrollStep {
  id: 'center' | 'left' | 'up' | 'down' | 'right';
  label: string;
}

@Component({ name: 'PositiveIdEnrollmentForm', components: { BiometricConsentDialog } })
export default class PositiveIdEnrollmentForm extends Vue {
  @Prop({ type: String, required: true }) readonly userName!: string;

  // Center first so a fundamental sensor / placement issue surfaces before we ask for tilts.
  // The DigitalPersona engine consolidates these into a single high-density enrollment FMD.
  private readonly ENROLL_STEPS: EnrollStep[] = [
    { id: 'center', label: 'Place finger naturally — flat on the reader' },
    { id: 'left',   label: 'Tilt finger LEFT' },
    { id: 'up',     label: 'Roll finger UP (tip down)' },
    { id: 'down',   label: 'Roll finger DOWN (pad up)' },
    { id: 'right',  label: 'Tilt finger RIGHT' },
  ];

  private credentials: PositiveIdCredential[] = [];
  private enrolling = false;

  // Badge capture
  private capturedBadge = '';
  private manualEntry = false;
  private manualBadge = '';

  // Fingerprint multi-sample enrollment state
  private enrollMode: EnrollMode = 'idle';
  private enrollSamples: string[] = [];

  // Biometric privacy consent state (BIPA / CUBI / WA HB 1493). Loaded per selected user; the
  // fingerprint enrollment UI gates on whether the subject has an active consent for the tenant's
  // current policy version.
  private consentStatus: BiometricConsentStatus | null = null;
  private showConsentDialog = false;

  // The hub serves both BadgeTap and FingerprintCaptured; one connection per form.
  private hub: signalR.HubConnection | null = null;
  private hubState: ReaderState = 'idle';

  private get effectiveBadge(): string {
    return this.manualEntry ? this.manualBadge.trim() : this.capturedBadge;
  }

  private get badgeStatus(): ReaderState { return this.hubState; }
  private get fingerprintStatus(): ReaderState { return this.hubState; }

  private get badgeStatusText(): string {
    switch (this.hubState) {
      case 'connecting': return 'Connecting to badge reader…';
      case 'connected': return this.capturedBadge ? 'Badge read.' : 'Tap the badge to enroll…';
      case 'unavailable': return 'Badge reader unavailable — enter badge ID manually.';
      default: return '';
    }
  }

  private get fingerprintStatusText(): string {
    if (this.hubState === 'connecting') return 'Connecting to fingerprint reader…';
    if (this.hubState === 'unavailable') return 'Fingerprint reader unavailable — install the HID DigitalPersona 4500 Non-WBF driver.';
    if (this.hubState !== 'connected') return '';
    if (this.enrollMode === 'submitting') return 'Submitting enrollment…';
    if (this.enrollMode === 'collecting') return 'Awaiting fingerprint placement…';
    return 'Reader ready.';
  }

  @Watch('userName', { immediate: true })
  async onUserNameChanged(newValue: string, oldValue?: string): Promise<void> {
    if (newValue === oldValue) return;
    await this.disconnectHub();
    this.reset();
    if (!newValue) return;
    await Promise.all([this.loadCredentials(), this.loadConsentStatus()]);
    await this.connectHub();
  }

  private reset(): void {
    this.capturedBadge = '';
    this.manualEntry = false;
    this.manualBadge = '';
    this.enrollMode = 'idle';
    this.enrollSamples = [];
    this.hubState = 'idle';
    this.credentials = [];
    this.consentStatus = null;
    this.showConsentDialog = false;
  }

  private async loadCredentials(): Promise<void> {
    if (!this.userName) return;
    try {
      this.credentials = await PositiveIdService.listCredentials(this.userName);
    } catch {
      this.$notification(NotificationOptions.error('Failed to load credentials.'));
    }
  }

  private async loadConsentStatus(): Promise<void> {
    if (!this.userName) return;
    try {
      this.consentStatus = await PositiveIdService.getBiometricConsentStatus(this.userName);
    } catch {
      this.$notification(NotificationOptions.error('Failed to load biometric consent status.'));
      this.consentStatus = null;
    }
  }

  // Called from BiometricConsentDialog after a successful consent post; refresh status so the
  // enrollment UI unlocks immediately without requiring a full reload.
  async onConsented(): Promise<void> {
    await this.loadConsentStatus();
  }

  private async connectHub(): Promise<void> {
    if (this.hub) return;
    this.hubState = 'connecting';
    try {
      this.hub = buildClientServiceHub();
      this.hub.on('BadgeTap', (evt: BadgeTapEvent) => {
        this.capturedBadge = evt?.cardDataHex ?? evt?.CardDataHex ?? '';
      });
      this.hub.on('FingerprintCaptured', (evt: FingerprintCapturedEvent) => {
        const template = evt?.templateBase64 ?? evt?.TemplateBase64 ?? '';
        if (template) void this.onFingerprintCaptured(template);
      });
      await this.hub.start();
      this.hubState = 'connected';
    } catch {
      this.hubState = 'unavailable';
      this.manualEntry = true;
      this.hub = null;
    }
  }

  private async disconnectHub(): Promise<void> {
    if (!this.hub) return;
    try { await this.hub.stop(); } catch { /* ignore */ }
    this.hub = null;
  }

  async enrollBadge(): Promise<void> {
    if (!this.effectiveBadge) return;
    this.enrolling = true;
    try {
      await PositiveIdService.enrollBadge(this.userName, this.effectiveBadge);
      this.$notification(NotificationOptions.success('Badge enrolled.'));
      this.capturedBadge = '';
      this.manualBadge = '';
      await this.loadCredentials();
      this.$emit('enrolled');
    } catch (err: any) {
      this.$notification(NotificationOptions.error(err?.response?.data ?? 'Failed to enroll badge.'));
    } finally {
      this.enrolling = false;
    }
  }

  startEnrollFingerprint(): void {
    this.enrollSamples = [];
    this.enrollMode = 'collecting';
  }

  cancelEnrollment(): void {
    if (this.enrollMode === 'submitting') return;
    this.enrollSamples = [];
    this.enrollMode = 'idle';
  }

  restartCurrentStep(): void {
    if (this.enrollMode !== 'collecting' || this.enrollSamples.length === 0) return;
    this.enrollSamples.pop();
  }

  // Fired by the SignalR `FingerprintCaptured` handler. Only consumes captures while we're
  // actively collecting; any placements while idle / submitting are ignored, which means a stray
  // finger tap won't accidentally start or corrupt an enrollment session.
  private async onFingerprintCaptured(template: string): Promise<void> {
    if (this.enrollMode !== 'collecting') return;
    if (this.enrollSamples.length >= this.ENROLL_STEPS.length) return;

    this.enrollSamples.push(template);
    if (this.enrollSamples.length < this.ENROLL_STEPS.length) return;

    // Last sample collected — submit the consolidated enrollment.
    await this.submitFingerprintEnrollment();
  }

  private async submitFingerprintEnrollment(): Promise<void> {
    this.enrollMode = 'submitting';
    this.enrolling = true;
    try {
      await PositiveIdService.enrollBiometric(this.userName, this.enrollSamples);
      this.$notification(NotificationOptions.success('Fingerprint enrolled.'));
      this.enrollSamples = [];
      this.enrollMode = 'idle';
      await this.loadCredentials();
      this.$emit('enrolled');
    } catch (err: any) {
      // Most common 400 path: the SDK couldn't consolidate the samples (e.g. they don't look like
      // the same finger). Surface the server message and reset so the user can retry cleanly.
      this.$notification(NotificationOptions.error(err?.response?.data ?? 'Failed to enroll fingerprint.'));
      this.enrollSamples = [];
      this.enrollMode = 'idle';
    } finally {
      this.enrolling = false;
    }
  }

  async revoke(credential: PositiveIdCredential): Promise<void> {
    try {
      await PositiveIdService.revokeCredential(credential.id);
      this.$notification(NotificationOptions.success('Credential revoked.'));
      await this.loadCredentials();
    } catch {
      this.$notification(NotificationOptions.error('Failed to revoke credential.'));
    }
  }

  private methodLabel(method: PositiveIdMethod): string {
    switch (method) {
      case PositiveIdMethod.Biometric: return 'Fingerprint';
      case PositiveIdMethod.BarCode: return 'Bar code';
      default: return 'Badge';
    }
  }

  private formatDate(iso: string): string {
    return iso ? new Date(iso).toLocaleString() : '';
  }

  beforeDestroy(): void {
    void this.disconnectHub();
  }
}
</script>

<style scoped>
.pi-reader-status { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; }
.pi-dot { width: 10px; height: 10px; border-radius: 50%; background: #adb5bd; }
.pi-status-connecting .pi-dot { background: #ffc107; }
.pi-status-connected .pi-dot { background: #28a745; }
.pi-status-unavailable .pi-dot { background: #dc3545; }

.pi-enroll-panel {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
  background: #fafbfc;
}
.pi-enroll-progress { font-weight: 600; margin-bottom: 8px; }
.pi-enroll-steps { list-style: none; padding: 0; margin: 0; }
.pi-enroll-steps li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 0.95rem;
  color: #6c757d;
}
.pi-enroll-steps li.pi-step-done { color: #495057; }
.pi-enroll-steps li.pi-step-current { color: #007bff; font-weight: 600; }
.pi-step-marker {
  display: inline-flex;
  width: 18px;
  justify-content: center;
}
.pi-step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ced4da;
}
</style>
